import React from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { Plus, X } from 'lucide-react';

import { dateFormat } from '../../../../helper';
import { useProgramForm } from '../../../../context/ProgramFormContext';
import { emptyAdditionalDate } from '../fields';
import SectionCard, { SubGroup } from '../ui/SectionCard';
import Field from '../ui/Field';
import Toggle from '../ui/Toggle';
import { NumberInput, DateInput, RangeInput } from '../ui/Inputs';
import { s, brand, gray, ink, white, radius } from '../theme';

/**
 * Section 3 — Price. Ports `AddProgramModal.js:1821-2119`.
 *
 * Two shapes behind one radio pair (`byDates`):
 *   2 — three registration windows (Early Bird / Standard / Late), one price each
 *   1 — a table of program date blocks, each with its own price and slot count
 *
 * Both render as tables: one column header over a run of bare rows, collapsing to stacked
 * cards below `lg`. The grid templates (`pf-tiers`, `pf-blocks`) live in `programForm.css`
 * because the collapse and the label hiding are media-query work.
 *
 * The three convenience-fee fields are gated on `props.program`, which is never supplied,
 * so they never render. The gate is kept rather than the fields removed — see the
 * decisions doc.
 */

const money = value => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// `props.program` is never passed to this form, exactly as in the modal. Kept as a named
// constant so the dead branches below stay visible instead of silently disappearing.
const SHOW_CONVENIENCE_FEES = false;

const tierPill = required => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: radius.md,
    padding: '4px 8px',
    fontSize: '11.5px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    backgroundColor: required ? brand[600] : gray[100],
    color: required ? white : ink[600],
});

const Price = () => {
    const {
        userInput,
        setUserInput,
        byDates,
        setByDates,
        additionalDates,
        setAdditionalDates,
        disableOvernight,
        setDisableOvernight,
        handleNumberChange,
        handleDateChange,
        fieldStatus,
        rowIssue,
        requiredOnly,
    } = useProgramForm();

    // ---- Program date rows ---------------------------------------------------
    const handleMultiDateChange = (e, i) => {
        const tz = userInput.programTimezone;
        const toTzMidnightUtc = momentDate =>
            momentTz.tz(momentDate.format('YYYY-MM-DD'), tz).valueOf();
        const toFormatted = momentDate => `${momentDate.format('YYYY-MM-DD')}T00:00:00.000Z`;

        const temp = [...additionalDates];
        temp[i].startDate = toTzMidnightUtc(e[0]);
        temp[i].endDate = toTzMidnightUtc(e[1]);
        temp[i].startDateFormatted = toFormatted(e[0]);
        temp[i].endDateFormatted = toFormatted(e[1]);
        temp[i].endDateErr = '';
        setAdditionalDates(temp);
    };

    const handleRowChange = (i, key, value, errKey) => {
        const temp = [...additionalDates];
        temp[i][key] = value;
        if (errKey) temp[i][errKey] = '';
        setAdditionalDates(temp);
    };

    const handleRemoveDate = index => {
        const temp = [...additionalDates];
        temp.splice(index, 1);
        setAdditionalDates(temp);
    };

    const handleAddDates = () => setAdditionalDates([...additionalDates, emptyAdditionalDate()]);

    const clearDates = () => {
        setAdditionalDates(additionalDates.map(() => emptyAdditionalDate()));

        setUserInput({
            dateEarlyRegisterOpen: '',
            dateEarlyRegisterClosed: '',
            registerOpenDate: '',
            registerCloseDate: '',
            dateLateRegisterOpen: '',
            dateLateRegisterClosed: '',
            startDate: '',
            endDate: '',
        });
    };

    const clearButton = (
        <button type="button" style={s.buttonGhost} onClick={clearDates}>
            Clear Dates
        </button>
    );

    /**
     * The three registration tiers.
     *
     * Every cross-tier rule the old stacked layout carried inline is expressed here, so one
     * row block can drive all three: Early Bird's end and price wait on its start date,
     * Standard cannot open before Early Bird closes, Late cannot open before Standard
     * closes. Built per render because each rule reads `userInput`.
     */
    const tiers = [
        {
            name: 'Early Bird',
            startKey: 'dateEarlyRegisterOpen',
            startLabel: 'Date Early Bird Registration Start',
            startDisabledDate: d =>
                userInput.registerOpenDate
                    ? d.isAfter(dateFormat(userInput.registerOpenDate + 86400000))
                    : d.isSameOrBefore(dateFormat(new Date().getTime())),
            endKey: 'dateEarlyRegisterClosed',
            endLabel: 'Date Early Bird Registration End',
            endDisabledDate: d =>
                d.isSameOrBefore(dateFormat(userInput.dateEarlyRegisterOpen + 86400000)),
            endDisabled: !userInput.dateEarlyRegisterOpen,
            priceKey: 'earlyBirdPrice',
            priceLabel: 'Early Bird Price',
            priceDisabled: !userInput.dateEarlyRegisterOpen,
            commuterKey: 'earlyBirdCommuterPrice',
            commuterLabel: 'Early Bird Commuter Price',
            commuterDisabled: !userInput.dateEarlyRegisterOpen,
            feeKey: 'earlyConvenienceFee',
            feeLabel: 'Early Convenience Fee',
        },
        {
            name: 'Standard',
            required: true,
            startKey: 'registerOpenDate',
            startLabel: 'Date Standard Registration Start',
            startDisabledDate: d =>
                userInput.dateEarlyRegisterOpen !== '' && userInput.dateEarlyRegisterClosed
                    ? d.isSameOrBefore(dateFormat(userInput.dateEarlyRegisterClosed + 86400000))
                    : d.isSameOrBefore(dateFormat(new Date().getTime())),
            endKey: 'registerCloseDate',
            endLabel: 'Date Standard Registration End',
            endDisabledDate: d => d.isSameOrBefore(dateFormat(userInput.registerOpenDate + 86400000)),
            endDisabled: !userInput.registerOpenDate,
            priceKey: 'registrationFee',
            priceLabel: 'Standard Price',
            commuterKey: 'commuterPrice',
            commuterLabel: 'Commuter Price',
            feeKey: 'convenienceFee',
            feeLabel: 'Convenience Fee',
        },
        {
            name: 'Late',
            startKey: 'dateLateRegisterOpen',
            startLabel: 'Date Late Registration Start',
            startDisabledDate: d =>
                d.isSameOrBefore(dateFormat(userInput.registerCloseDate + 86400000)),
            endKey: 'dateLateRegisterClosed',
            endLabel: 'Date Late Registration End',
            endDisabledDate: d =>
                d.isSameOrBefore(dateFormat(userInput.dateLateRegisterOpen + 86400000)),
            endDisabled: !userInput.dateLateRegisterOpen,
            priceKey: 'lateRegistrationFee',
            priceLabel: 'Late Registration Price',
            priceDisabled: !userInput.dateLateRegisterOpen,
            commuterKey: 'lateCommuterPrice',
            commuterLabel: 'Late Commuter Price',
            commuterDisabled: !userInput.dateLateRegisterOpen,
            feeKey: 'lateConvenienceFee',
            feeLabel: 'Late Convenience Fee',
        },
    ];

    // Early Bird and Late are entirely optional, so the "show required only" filter drops
    // them — same as when they were three separate groups.
    const visibleTiers = requiredOnly ? tiers.filter(tier => tier.required) : tiers;

    const dateValue = key => (userInput[key] ? moment(dateFormat(parseInt(userInput[key]))) : '');

    return (
        <SectionCard
            id="price"
            step={3}
            title="Price"
            description="Registration windows, pricing tiers and capacity"
        >
            {/* Price basis */}
            <div className="mb-4">
                <p style={{ ...s.label, marginBottom: '8px' }}>Program price is based on?</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                        { value: 2, label: 'Registration Dates' },
                        { value: 1, label: 'Program Dates' },
                    ].map(option => (
                        <label
                            key={option.value}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: 0,
                                padding: '8px 14px',
                                fontSize: '13px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                borderRadius: '999px',
                                border: `1px solid ${byDates === option.value ? brand[600] : gray[300]}`,
                                backgroundColor: byDates === option.value ? brand[50] : '#FFFFFF',
                                color: byDates === option.value ? brand[700] : ink[600],
                            }}
                        >
                            <input
                                type="radio"
                                name="byDates"
                                checked={byDates === option.value}
                                onChange={() => setByDates(option.value)}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            </div>

            <div className="row">
                {!requiredOnly ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="Non-refundable registration fee"
                        htmlFor="registerNonRefundFee"
                        {...fieldStatus('registerNonRefundFee')}
                    >
                        <NumberInput
                            id="registerNonRefundFee"
                            formatter={money}
                            change={e => handleNumberChange(e, 'registerNonRefundFee')}
                            value={userInput.registerNonRefundFee}
                            min={0}
                            precision={userInput.registerNonRefundFee ? 2 : 0}
                            pHolder="Non-refundable registration fee"
                        />
                    </Field>
                ) : ''}

                {byDates !== 1 ? (
                    <Field
                        className="col-md-6 mb-3"
                        label="No. of Available slots"
                        htmlFor="noOfSlots"
                        required
                        {...fieldStatus('noOfSlots')}
                    >
                        <NumberInput
                            id="noOfSlots"
                            change={e => handleNumberChange(e, 'noOfSlots')}
                            value={userInput.noOfSlots}
                            pHolder="No. of Available slots"
                            min={0}
                            precision={0}
                            step={1}
                            error={fieldStatus('noOfSlots').error}
                        />
                    </Field>
                ) : ''}
            </div>

            {byDates !== 1 ? (
                <SubGroup
                    title="Registration windows"
                    description="Each tier opens when the previous one closes. Leave an Early Bird or Late commuter price blank to charge the standard commuter price."
                    action={clearButton}
                >
                    <div className="pf-head pf-tiers">
                        <span style={s.groupTitle}>Tier</span>
                        <span style={s.groupTitle}>Registration start</span>
                        <span style={s.groupTitle}>Registration end</span>
                        <span style={s.groupTitle}>Price</span>
                        <span style={s.groupTitle}>Commuter price</span>
                    </div>

                    {visibleTiers.map(tier => (
                        <div key={tier.name} className="pf-row pf-tiers">
                            <span style={{ alignSelf: 'center' }}>
                                <span style={tierPill(tier.required)}>{tier.name}</span>
                                {tier.required ? (
                                    <span style={{ marginLeft: '4px', color: brand[600] }} aria-hidden="true">
                                        *
                                    </span>
                                ) : ''}
                            </span>

                            <Field
                                className="pf-hide-label"
                                label={tier.startLabel}
                                htmlFor={tier.startKey}
                                required={tier.required}
                                {...fieldStatus(tier.startKey)}
                            >
                                <DateInput
                                    id={tier.startKey}
                                    disabledDate={tier.startDisabledDate}
                                    value={dateValue(tier.startKey)}
                                    change={e => handleDateChange(e, tier.startKey)}
                                    error={fieldStatus(tier.startKey).error}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={tier.endLabel}
                                htmlFor={tier.endKey}
                                required={tier.required}
                                {...fieldStatus(tier.endKey)}
                            >
                                <DateInput
                                    id={tier.endKey}
                                    disabledDate={tier.endDisabledDate}
                                    disabled={tier.endDisabled}
                                    value={dateValue(tier.endKey)}
                                    change={e => handleDateChange(e, tier.endKey)}
                                    error={fieldStatus(tier.endKey).error}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={tier.priceLabel}
                                htmlFor={tier.priceKey}
                                required={tier.required}
                                {...fieldStatus(tier.priceKey)}
                            >
                                <NumberInput
                                    id={tier.priceKey}
                                    formatter={money}
                                    change={e => handleNumberChange(e, tier.priceKey)}
                                    value={userInput[tier.priceKey]}
                                    disabled={tier.priceDisabled}
                                    min={0}
                                    precision={userInput[tier.priceKey] ? 2 : 0}
                                    pHolder={tier.priceLabel}
                                    error={fieldStatus(tier.priceKey).error}
                                />
                            </Field>

                            {/* Standard is the program's commuter rate; Early Bird and Late are
                                optional overrides that inherit from it when left blank. Sending
                                0 is what the server reads as "inherit", so a blank box needs no
                                special handling. */}
                            <Field
                                className="pf-hide-label"
                                label={tier.commuterLabel}
                                htmlFor={tier.commuterKey}
                                {...fieldStatus(tier.commuterKey)}
                            >
                                <NumberInput
                                    id={tier.commuterKey}
                                    formatter={money}
                                    change={e => handleNumberChange(e, tier.commuterKey)}
                                    value={userInput[tier.commuterKey]}
                                    disabled={tier.commuterDisabled}
                                    min={0}
                                    precision={userInput[tier.commuterKey] ? 2 : 0}
                                    pHolder={
                                        tier.required
                                            ? 'Commuter Price'
                                            : (Number(userInput.commuterPrice) > 0
                                                ? `Same as $${userInput.commuterPrice}`
                                                : 'No commuter price')
                                    }

                                    error={fieldStatus(tier.commuterKey).error}
                                />
                            </Field>

                            {SHOW_CONVENIENCE_FEES ? (
                                <Field
                                    className="pf-hide-label"
                                    label={tier.feeLabel}
                                    htmlFor={tier.feeKey}
                                >
                                    <NumberInput
                                        id={tier.feeKey}
                                        formatter={money}
                                        change={e => handleNumberChange(e, tier.feeKey)}
                                        value={userInput[tier.feeKey]}
                                        min={0}
                                        precision={userInput[tier.feeKey] ? 2 : 0}
                                        pHolder={tier.feeLabel}
                                    />
                                </Field>
                            ) : ''}
                        </div>
                    ))}

                    <div className="mt-3">
                        <Toggle
                            id="disableOvernight"
                            label="Disable Overnight"
                            description="Only commuter registrations will be accepted for this program."
                            checked={Boolean(disableOvernight)}
                            onChange={checked => setDisableOvernight(checked ? 1 : 0)}
                            labelStyle={{ color: ink[700] }}
                        />
                    </div>
                </SubGroup>
            ) : (
                /* Program dates: one priced block per row */
                <SubGroup
                    title="Program dates & pricing"
                    description="Each block is a bookable date range with its own price and capacity."
                    action={clearButton}
                >
                    <div className="pf-head pf-blocks">
                        <span style={s.groupTitle}>
                            Start date — end date<span style={{ color: brand[600] }}>*</span>
                        </span>
                        <span style={s.groupTitle}>
                            Standard price<span style={{ color: brand[600] }}>*</span>
                        </span>
                        <span style={s.groupTitle}>
                            Available slots<span style={{ color: brand[600] }}>*</span>
                        </span>
                        <span style={s.groupTitle}>Commuter price</span>
                        <span style={s.srOnly}>Remove</span>
                    </div>

                    {additionalDates.map((aDate, i) => (
                        <div key={i} className="pf-row pf-blocks">
                            <Field
                                className="pf-hide-label"
                                label={`Start / End Date for block ${i + 1}`}
                                htmlFor={`aDate-${i}`}
                                required
                                issue={
                                    aDate.endDateErr
                                        ? { severity: 'error', message: aDate.endDateErr }
                                        : rowIssue(i, 'endDate') || rowIssue(i, 'startDate')
                                }
                            >
                                <RangeInput
                                    id={`aDate-${i}`}
                                    error={Boolean(aDate.endDateErr)}
                                    value={
                                        aDate.startDateFormatted
                                            ? [moment.utc(aDate.startDateFormatted), moment.utc(aDate.endDateFormatted)]
                                            : ''
                                    }
                                    change={e => handleMultiDateChange(e, i)}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={`Standard Price for block ${i + 1}`}
                                htmlFor={`aDatePrice-${i}`}
                                required
                                issue={
                                    aDate.standardPriceErr
                                        ? { severity: 'error', message: aDate.standardPriceErr }
                                        : undefined
                                }
                            >
                                <NumberInput
                                    id={`aDatePrice-${i}`}
                                    formatter={money}
                                    change={e => handleRowChange(i, 'standardPrice', e, 'standardPriceErr')}
                                    value={aDate.standardPrice}
                                    pHolder="Standard Price"
                                    min={0}
                                    precision={aDate.standardPrice ? 2 : 0}
                                    error={Boolean(aDate.standardPriceErr)}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={`Available slots for block ${i + 1}`}
                                htmlFor={`aDateSlots-${i}`}
                                required
                                issue={
                                    aDate.slotsErr ? { severity: 'error', message: aDate.slotsErr } : undefined
                                }
                            >
                                <NumberInput
                                    id={`aDateSlots-${i}`}
                                    change={e => handleRowChange(i, 'slots', e, 'slotsErr')}
                                    value={aDate.slots}
                                    pHolder="Slots"
                                    min={0}
                                    precision={0}
                                    step={1}
                                    error={Boolean(aDate.slotsErr)}
                                />
                            </Field>

                            <Field
                                className="pf-hide-label"
                                label={`Commuter Price for block ${i + 1}`}
                                htmlFor={`aDateCommuter-${i}`}
                            >
                                <NumberInput
                                    id={`aDateCommuter-${i}`}
                                    formatter={money}
                                    change={e => handleRowChange(i, 'commuterPrice', e)}
                                    value={aDate.commuterPrice}
                                    pHolder="Commuter"
                                    min={0}
                                />
                            </Field>

                            <button
                                type="button"
                                onClick={() => handleRemoveDate(i)}
                                aria-label={`Remove date block ${i + 1}`}
                                style={{
                                    alignSelf: 'center',
                                    justifySelf: 'start',
                                    background: 'none',
                                    border: 'none',
                                    padding: '6px',
                                    color: ink[400],
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = brand[600]; }}
                                onMouseLeave={e => { e.currentTarget.style.color = ink[400]; }}
                            >
                                <X size={16} aria-hidden="true" />
                            </button>
                        </div>
                    ))}

                    <button type="button" style={s.buttonGhost} onClick={handleAddDates}>
                        <Plus size={14} aria-hidden="true" />
                        Add Dates
                    </button>
                </SubGroup>
            )}
        </SectionCard>
    );
};

export default Price;
